import { useEffect, useState, useCallback, useRef } from "react";
import { Select, Spin, Pagination, AutoComplete, Modal } from "antd";
import { message } from "antd";
import {
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import API from "../../services/api";
import { Row, Col, Empty } from "antd";
import { Link } from "react-router-dom";
import "./Adss.css";
import { filterCities, getLatLngForFilter } from "../locationService";
import {
  validateLocality,
  validateNumericRange,
  isEmpty,
} from "../../utils/Filtervalidation.js";
import { useFilterValidation } from "../../hooks/Usefiltervalidation.js";
// ─────────────────────────────────────────────────────────────────────────────
// NEW: import the reusable location hook
// ─────────────────────────────────────────────────────────────────────────────
import { useLocationSearch } from "../../hooks/Uselocationsearch.js";

// ─────────────────────────────────────────────────────────────────────────────
// ALLOWED VALUES
// ─────────────────────────────────────────────────────────────────────────────
const ALLOWED_CATEGORIES = [
  "billboard", "vehicle", "walking", "wall", "shop_front", "bus_stop", "pole",
];
const ALLOWED_TRAFFIC_LEVELS = ["low", "medium", "high"];
const ALLOWED_AUDIENCE_TYPES = ["pedestrian", "vehicle"];
const ALLOWED_PRICING_UNITS = ["per_day", "per_week", "per_month"];
const ALLOWED_PLACEMENT_POSITIONS = ["front", "back", "side", "top", "full_wrap"];
const ALLOWED_VEHICLE_TYPES = ["bike", "car", "bus"];
const MAX_PRICE = 1_000_000_000;

// ─────────────────────────────────────────────────────────────────────────────
// ValidationTooltip
// ─────────────────────────────────────────────────────────────────────────────
function ValidationTooltip({ message }) {
  if (!message) return null;
  return (
    <div className="vt-tooltip" role="alert" aria-live="polite">
      <span className="vt-tooltip-icon" aria-hidden="true" />
      <span className="vt-tooltip-text">{message}</span>
    </div>
  );
}

function Adss() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // ── Search-bar suggestion state (driven by useLocationSearch hook)
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  // ── Filter-modal locality suggestion state (separate, unchanged)
  const [filterSuggestions, setFilterSuggestions] = useState([]);

  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  const [trafficDropdownOpen, setTrafficDropdownOpen] = useState(false);

  const [audienceDropdownOpen, setAudienceDropdownOpen] = useState(false);

  const [pricingDropdownOpen, setPricingDropdownOpen] = useState(false);

  const [placementDropdownOpen, setPlacementDropdownOpen] = useState(false);

  const [negotiableDropdownOpen, setNegotiableDropdownOpen] = useState(false);

  // ── Validation hook ────────────────────────────────────────────────────────
  const { fieldErrors, setFieldErrors, clearAllFieldErrors, scrollToFirstError } =
    useFilterValidation();

  // ── Refs for scroll-to-error ───────────────────────────────────────────────
  const localityRef = useRef(null);
  const priceRef = useRef(null);

  // filterDebounceRef is still used by the filter-modal locality field
  const filterDebounceRef = useRef(null);

  const filterModalBodyRef = useRef(null);

  const searchDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);


  const [filters, setFilters] = useState({
    search: "",
    locality: "",
    latitude: null,
    longitude: null,
    category: "",
    minPrice: "",
    maxPrice: "",
    trafficLevel: "",
    audienceType: [],
    pricingUnit: "",
    placementPosition: "",
    vehicleType: "",
    negotiable: null,
  });

  // ── NEW: reusable location-search hook for the main search bar ─────────────
  const {
    inputValue: searchInputValue,
    isLocationValid: isSearchLocationValid,
    handleChange: handleSearchChange,
    handleSelect: handleSearchSelect,
    handleKeyDown: makeSearchKeyDown,
    handleFocus: handleSearchFocus,
    resetLocation: resetSearchLocation,
  } = useLocationSearch({
    onSuggestionsChange: setSearchSuggestions,
    debounceMs: 300,
    minChars: 2,

    // SYNC FILTER → SEARCH
    syncedLocality: filters.locality,

    // SYNC SEARCH → FILTER
    onSyncLocality: (value) => {

      setFilters((prev) => {

        if (prev.locality === value) {
          return prev;
        }

        return {
          ...prev,
          locality: value,
        };
      });
    },
  });


  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  useEffect(() => {
    const handleOutsideClick = (e) => {

      // SEARCH DROPDOWN
      const clickedSearchArea = e.target.closest(
        ".ad-search-field"
      );

      if (!clickedSearchArea) {
        setSearchSuggestions([]);
      }

      // FILTER LOCALITY DROPDOWN
      const clickedFilterArea = e.target.closest(
        ".ad-filter-group"
      );

      if (!clickedFilterArea) {
        setFilterSuggestions([]);
      }

      // CUSTOM DROPDOWNS
      if (!e.target.closest(".ad-custom-dropdown")) {

        setCategoryDropdownOpen(false);

        setTrafficDropdownOpen(false);

        setAudienceDropdownOpen(false);

        setPricingDropdownOpen(false);

        setPlacementDropdownOpen(false);

        setNegotiableDropdownOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (filterOpen) {
      document.body.style.overflow = "hidden";

      setTimeout(() => {
        const modalBody =
          filterModalBodyRef.current?.querySelector(
            ".ant-modal-body"
          );

        if (modalBody) {
          modalBody.scrollTo({
            top: 0,
            behavior: "auto",
          });
        }
      }, 100);

    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [filterOpen]);

  // ── Sanitize numeric input ────────────────────────────────────────────────
  const sanitizeNumericInput = (value, max) => {
    if (value === "" || value === null || value === undefined) {
      return "";
    }

    // ONLY positive integers allowed
    const stripped = String(value).replace(/[^0-9]/g, "");

    if (stripped === "") {
      return "";
    }

    const n = Number(stripped);

    if (!Number.isInteger(n) || n < 0) {
      return "";
    }

    if (max !== undefined && n > max) {
      return String(max);
    }

    return stripped;
  };

  // ── Build clean params ────────────────────────────────────────────────────
  const buildParams = useCallback((customFilters, page, pageSize) => {
    const params = { page, limit: pageSize };

    if (customFilters.search?.trim()) {
      params.search = customFilters.search.trim().slice(0, 200);
    }
    if (customFilters.locality?.trim()) {
      params.locality = customFilters.locality.trim().slice(0, 100);
    }
    if (customFilters.category && ALLOWED_CATEGORIES.includes(customFilters.category)) {
      params.category = customFilters.category;
    }

    const minPrice = Number(customFilters.minPrice);
    const maxPrice = Number(customFilters.maxPrice);
    if (customFilters.minPrice !== "" && isFinite(minPrice) && minPrice >= 0) {
      params.minPrice = minPrice;
    }
    if (customFilters.maxPrice !== "" && isFinite(maxPrice) && maxPrice >= 0) {
      params.maxPrice = maxPrice;
    }

    if (
      customFilters.trafficLevel &&
      ALLOWED_TRAFFIC_LEVELS.includes(customFilters.trafficLevel)
    ) {
      params.trafficLevel = customFilters.trafficLevel;
    }

    if (Array.isArray(customFilters.audienceType) && customFilters.audienceType.length) {
      const safe = customFilters.audienceType.filter((a) =>
        ALLOWED_AUDIENCE_TYPES.includes(a)
      );
      if (safe.length) params.audienceType = safe;
    }

    if (
      customFilters.pricingUnit &&
      ALLOWED_PRICING_UNITS.includes(customFilters.pricingUnit)
    ) {
      params.pricingUnit = customFilters.pricingUnit;
    }

    if (
      customFilters.placementPosition &&
      ALLOWED_PLACEMENT_POSITIONS.includes(customFilters.placementPosition)
    ) {
      params.placementPosition = customFilters.placementPosition;
    }

    if (
      customFilters.vehicleType &&
      ALLOWED_VEHICLE_TYPES.includes(customFilters.vehicleType)
    ) {
      params.vehicleType = customFilters.vehicleType;
    }

    if (customFilters.negotiable !== null && customFilters.negotiable !== undefined) {
      params.negotiable = customFilters.negotiable;
    }

    return params;
  }, []);

  const fetchAds = useCallback(
    async (customFilters = filters, page = 1, pageSize = 15) => {
      try {
        setLoading(true);
        const params = buildParams(customFilters, page, pageSize);
        const res = await API.get("/ads", { params });
        setAds(res.data.ads || []);
        setPagination({
          current: res.data.currentPage || 1,
          pageSize,
          total: res.data.total || 0,
        });
      } catch (err) {
        console.error("Fetch error:", err);
        setAds([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchAds(filters, 1, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Run per-field validation, collect errors, scroll to first ────────────
  const runFieldValidations = (filtersToValidate) => {
    const errors = {};

    const localityResult = validateLocality(filtersToValidate.locality);
    if (!localityResult.valid) errors.locality = localityResult.message;

    const priceResult = validateNumericRange(
      filtersToValidate.minPrice,
      filtersToValidate.maxPrice,
      "Price"
    );
    if (!priceResult.valid) errors.price = priceResult.message;

    setFieldErrors(errors);

    const refMap = {
      locality: localityRef,
      price: priceRef,
    };
    scrollToFirstError(refMap, errors);

    return Object.keys(errors).length === 0;
  };

  // ── REAL‑TIME ERROR CLEARING ─────────────────────────────────────────────
  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;

    const newErrors = { ...fieldErrors };

    // Locality error
    if (newErrors.locality) {
      const localityResult = validateLocality(filters.locality);
      if (localityResult.valid) delete newErrors.locality;
    }

    // Price error (min/max)
    if (newErrors.price) {
      const priceResult = validateNumericRange(filters.minPrice, filters.maxPrice, "Price");
      if (priceResult.valid) delete newErrors.price;
    }

    if (Object.keys(newErrors).length !== Object.keys(fieldErrors).length) {
      setFieldErrors(newErrors);
    }
  }, [filters.locality, filters.minPrice, filters.maxPrice, fieldErrors, setFieldErrors]);

  // ── handleApplyFilters ────────────────────────────────────────────────────
  // Called ONLY by the Search button click or Enter key (when valid).
  // For the main search bar, this uses searchInputValue (the typed/selected value).
  // For the filter modal, it uses filters.locality as before.
  const handleApplyFilters = async () => {
    const isValid = runFieldValidations(filters);
    if (!isValid) return;

    let updatedFilters = {
      ...filters,
      search: "",
      locality: filters.locality?.trim() || "",
    };

    if (!updatedFilters.locality) {
      updatedFilters.latitude = null;
      updatedFilters.longitude = null;
    }

    setFilters(updatedFilters);
    setSearchSuggestions([]);
    clearAllFieldErrors();
    fetchAds(updatedFilters, 1, pagination.pageSize);
    setFilterOpen(false);
  };

  // ── handleSearchBarApply ──────────────────────────────────────────────────
  // Called ONLY when the Search button (main bar) is clicked, or Enter is
  // pressed AND isSearchLocationValid is true.
  // Builds filters from the search bar's selected location value.
  const handleSearchBarApply = async () => {

    const selectedValue = searchInputValue.trim();

    const updatedFilters = {
      search: selectedValue,
      locality: "",
      latitude: null,
      longitude: null,
      category: "",
      minPrice: "",
      maxPrice: "",
      trafficLevel: "",
      audienceType: [],
      pricingUnit: "",
      placementPosition: "",
      vehicleType: "",
      negotiable: null,
    };

    setFilters(updatedFilters);

    setSearchSuggestions([]);

    clearAllFieldErrors();

    await fetchAds(
      updatedFilters,
      1,
      pagination.pageSize
    );
  };

  const handleReset = () => {
    const resetFilters = {
      search: "",
      locality: "",
      latitude: null,
      longitude: null,
      category: "",
      minPrice: "",
      maxPrice: "",
      trafficLevel: "",
      audienceType: [],
      pricingUnit: "",
      placementPosition: "",
      vehicleType: "",
      negotiable: null,
    };
    setFilters(resetFilters);
    resetSearchLocation();
    clearAllFieldErrors();
    fetchAds(resetFilters, 1, pagination.pageSize);
    const modalBody =
      filterModalBodyRef.current?.querySelector(
        ".ant-modal-body"
      );

    if (modalBody) {
      modalBody.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handlePageChange = (page) => {
    fetchAds(filters, page, 15);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeFiltersCount = [
    filters.locality,
    filters.category,
    filters.minPrice,
    filters.maxPrice,
    filters.trafficLevel,
    filters.audienceType?.length,
    filters.pricingUnit,
    filters.placementPosition,
    filters.vehicleType,
    filters.negotiable !== null ? 1 : 0,
  ].filter(Boolean).length;

  const filterDrawer = (
    <div className="ad-filter-panel">
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Locality */}
        <div className="ad-filter-group vt-field-wrap" ref={localityRef}>
          <label className="ad-filter-label">Locality / City</label>
          <div
            className="ad-search-custom"
            ref={searchDropdownRef}
          >
            <input
              type="text"
              className={`ad-filter-input${fieldErrors.locality ? " vt-input-error" : ""
                }`}
              placeholder="Search locality or city"
              maxLength={100}
              value={filters.locality}
              onChange={(e) => {
                const value = e.target.value;

                setFilters((prev) => ({
                  ...prev,
                  locality: value,
                  latitude: null,
                  longitude: null,
                }));

                clearTimeout(filterDebounceRef.current);

                if (!value || value.length < 2) {
                  setFilterSuggestions([]);
                  return;
                }

                filterDebounceRef.current = setTimeout(async () => {
                  try {
                    const results = await filterCities(value);
                    setFilterSuggestions(results || []);
                  } catch (err) {
                    console.error(err);
                  }
                }, 500);
              }}
            />

            {filterSuggestions.length > 0 && (
              <div className="ad-search-dropdown-custom">
                {filterSuggestions.map((item, index) => (
                  <div
                    key={index}
                    className="ad-search-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        locality: item.value,
                        latitude: null,
                        longitude: null,
                      }));

                      setFilterSuggestions([]);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <ValidationTooltip message={fieldErrors.locality} />
        </div>

        {/* Category */}
        <div className="ad-filter-group">
          <label className="ad-filter-label">
            Ad Category
          </label>

          <div className="ad-custom-dropdown">
            <div
              className={`ad-custom-dropdown-input ${categoryDropdownOpen ? "active" : ""
                }`}
              onClick={() => {
                setCategoryDropdownOpen(
                  !categoryDropdownOpen
                );

                setTrafficDropdownOpen(false);
                setAudienceDropdownOpen(false);
                setPricingDropdownOpen(false);
                setPlacementDropdownOpen(false);
                setNegotiableDropdownOpen(false);
              }}
            >
              <span>
                {filters.category
                  ? filters.category
                    .replaceAll("_", " ")
                    .replace(/\b\w/g, (c) =>
                      c.toUpperCase()
                    )
                  : "Select category"}
              </span>

              <span
                className={`ad-dropdown-arrow ${categoryDropdownOpen
                  ? "rotate"
                  : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {categoryDropdownOpen && (
              <div className="ad-custom-dropdown-menu">
                {[
                  {
                    value: "billboard",
                    label: "Billboard",
                  },
                  {
                    value: "vehicle",
                    label: "Vehicle",
                  },
                  {
                    value: "walking",
                    label: "Walking",
                  },
                  {
                    value: "wall",
                    label: "Wall",
                  },
                  {
                    value: "shop_front",
                    label: "Shop Front",
                  },
                  {
                    value: "bus_stop",
                    label: "Bus Stop",
                  },
                  {
                    value: "pole",
                    label: "Pole",
                  },
                ].map((item) => (
                  <div
                    key={item.value}
                    className={`ad-custom-dropdown-item ${filters.category ===
                      item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        category: item.value,
                      }));

                      setCategoryDropdownOpen(
                        false
                      );
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Price Range — both min/max grouped under one error */}
        <div className="ad-filter-group vt-field-wrap" ref={priceRef}>
          <label className="ad-filter-label">Min Price (₹)</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Min ₹"
            min={0}
            max={MAX_PRICE}
            value={filters.minPrice}
            className={`ad-filter-input${fieldErrors.price ? " vt-input-error" : ""}`}
            onChange={(e) => {
              const val = sanitizeNumericInput(e.target.value, MAX_PRICE);
              setFilters((prev) => ({ ...prev, minPrice: val }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleApplyFilters();
            }}
          />
        </div>

        <div className="ad-filter-group vt-field-wrap">
          <label className="ad-filter-label">Max Price (₹)</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Max ₹"
            min={0}
            max={MAX_PRICE}
            value={filters.maxPrice}
            className={`ad-filter-input${fieldErrors.price ? " vt-input-error" : ""}`}
            onChange={(e) => {
              const val = sanitizeNumericInput(e.target.value, MAX_PRICE);
              setFilters((prev) => ({ ...prev, maxPrice: val }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleApplyFilters();
            }}
          />
          {/* Show the price error under Max Price (last in pair) */}
          <ValidationTooltip message={fieldErrors.price} />
        </div>

        {/* Traffic Level */}
        <div className="ad-filter-group">
          <label className="ad-filter-label">
            Traffic Level
          </label>

          <div className="ad-custom-dropdown">
            <div
              className={`ad-custom-dropdown-input ${trafficDropdownOpen ? "active" : ""
                }`}
              onClick={() => {
                setTrafficDropdownOpen(
                  !trafficDropdownOpen
                );

                setCategoryDropdownOpen(false);
                setAudienceDropdownOpen(false);
                setPricingDropdownOpen(false);
                setPlacementDropdownOpen(false);
                setNegotiableDropdownOpen(false);
              }}
            >
              <span>
                {filters.trafficLevel
                  ? filters.trafficLevel
                    .charAt(0)
                    .toUpperCase() +
                  filters.trafficLevel.slice(1)
                  : "Select traffic level"}
              </span>

              <span
                className={`ad-dropdown-arrow ${trafficDropdownOpen
                  ? "rotate"
                  : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {trafficDropdownOpen && (
              <div className="ad-custom-dropdown-menu">
                {[
                  {
                    value: "low",
                    label: "Low",
                  },
                  {
                    value: "medium",
                    label: "Medium",
                  },
                  {
                    value: "high",
                    label: "High",
                  },
                ].map((item) => (
                  <div
                    key={item.value}
                    className={`ad-custom-dropdown-item ${filters.trafficLevel ===
                      item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        trafficLevel:
                          item.value,
                      }));

                      setTrafficDropdownOpen(
                        false
                      );
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Audience Type */}
        <div className="ad-filter-group">
          <label className="ad-filter-label">
            Audience Type
          </label>

          <div className="ad-custom-dropdown">
            <div
              className={`ad-custom-dropdown-input ${audienceDropdownOpen
                ? "active"
                : ""
                }`}
              onClick={() => {
                setAudienceDropdownOpen(
                  !audienceDropdownOpen
                );

                setCategoryDropdownOpen(false);
                setTrafficDropdownOpen(false);
                setPricingDropdownOpen(false);
                setPlacementDropdownOpen(false);
                setNegotiableDropdownOpen(false);
              }}
            >
              <span>
                {filters.audienceType
                  ?.length
                  ? `${filters.audienceType.length} selected`
                  : "Select audience"}
              </span>

              <span
                className={`ad-dropdown-arrow ${audienceDropdownOpen
                  ? "rotate"
                  : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {audienceDropdownOpen && (
              <div className="ad-custom-dropdown-menu">
                {[
                  {
                    value: "pedestrian",
                    label: "Pedestrian",
                  },
                  {
                    value: "vehicle",
                    label: "Vehicle",
                  },
                ].map((item) => {
                  const isSelected =
                    filters.audienceType.includes(
                      item.value
                    );

                  return (
                    <div
                      key={item.value}
                      className={`ad-custom-dropdown-item ${isSelected
                        ? "selected"
                        : ""
                        }`}
                      onClick={() => {
                        let updated = [
                          ...filters.audienceType,
                        ];

                        if (
                          updated.includes(
                            item.value
                          )
                        ) {
                          updated =
                            updated.filter(
                              (v) =>
                                v !== item.value
                            );
                        } else {
                          updated.push(
                            item.value
                          );
                        }

                        setFilters(
                          (prev) => ({
                            ...prev,
                            audienceType:
                              updated,
                          })
                        );
                      }}
                    >
                      {item.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Pricing Unit */}
        <div className="ad-filter-group">
          <label className="ad-filter-label">
            Pricing Unit
          </label>

          <div className="ad-custom-dropdown">
            <div
              className={`ad-custom-dropdown-input ${pricingDropdownOpen ? "active" : ""
                }`}
              onClick={() => {
                setPricingDropdownOpen(
                  !pricingDropdownOpen
                );

                setCategoryDropdownOpen(false);
                setTrafficDropdownOpen(false);
                setAudienceDropdownOpen(false);
                setPlacementDropdownOpen(false);
                setNegotiableDropdownOpen(false);
              }}
            >
              <span>
                {filters.pricingUnit
                  ? filters.pricingUnit
                    .replaceAll("_", " ")
                    .replace(/\b\w/g, (c) =>
                      c.toUpperCase()
                    )
                  : "Select pricing unit"}
              </span>

              <span
                className={`ad-dropdown-arrow ${pricingDropdownOpen
                  ? "rotate"
                  : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {pricingDropdownOpen && (
              <div className="ad-custom-dropdown-menu">
                {[
                  {
                    value: "per_day",
                    label: "Per Day",
                  },
                  {
                    value: "per_week",
                    label: "Per Week",
                  },
                  {
                    value: "per_month",
                    label: "Per Month",
                  },
                ].map((item) => (
                  <div
                    key={item.value}
                    className={`ad-custom-dropdown-item ${filters.pricingUnit ===
                      item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        pricingUnit:
                          item.value,
                      }));

                      setPricingDropdownOpen(
                        false
                      );
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Placement Position */}
        <div className="ad-filter-group">
          <label className="ad-filter-label">
            Placement Position
          </label>

          <div className="ad-custom-dropdown">
            <div
              className={`ad-custom-dropdown-input ${placementDropdownOpen
                ? "active"
                : ""
                }`}
              onClick={() => {
                setPlacementDropdownOpen(
                  !placementDropdownOpen
                );

                setCategoryDropdownOpen(false);
                setTrafficDropdownOpen(false);
                setAudienceDropdownOpen(false);
                setPricingDropdownOpen(false);
                setNegotiableDropdownOpen(false);
              }}
            >
              <span>
                {filters.placementPosition
                  ? filters.placementPosition
                    .replaceAll("_", " ")
                    .replace(/\b\w/g, (c) =>
                      c.toUpperCase()
                    )
                  : "Select placement"}
              </span>

              <span
                className={`ad-dropdown-arrow ${placementDropdownOpen
                  ? "rotate"
                  : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {placementDropdownOpen && (
              <div className="ad-custom-dropdown-menu">
                {[
                  {
                    value: "front",
                    label: "Front",
                  },
                  {
                    value: "back",
                    label: "Back",
                  },
                  {
                    value: "side",
                    label: "Side",
                  },
                  {
                    value: "top",
                    label: "Top",
                  },
                  {
                    value: "full_wrap",
                    label: "Full Wrap",
                  },
                ].map((item) => (
                  <div
                    key={item.value}
                    className={`ad-custom-dropdown-item ${filters.placementPosition ===
                      item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        placementPosition:
                          item.value,
                      }));

                      setPlacementDropdownOpen(
                        false
                      );
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Type — only shown for vehicle category */}
        {filters.category === "vehicle" && (
          <div className="ad-filter-group">
            <label className="ad-filter-label">Vehicle Type</label>
            <Select
              allowClear
              style={{ width: "100%" }}
              value={filters.vehicleType || undefined}
              onChange={(v) =>
                setFilters((p) => ({
                  ...p,
                  vehicleType: ALLOWED_VEHICLE_TYPES.includes(v) ? v : "",
                }))
              }
              options={[
                { value: "bike", label: "Bike" },
                { value: "car", label: "Car" },
                { value: "bus", label: "Bus" },
              ]}
            />
          </div>
        )}

        {/* Negotiable */}
        <div className="ad-filter-group">
          <label className="ad-filter-label">
            Negotiable
          </label>

          <div className="ad-custom-dropdown">
            <div
              className={`ad-custom-dropdown-input ${negotiableDropdownOpen
                ? "active"
                : ""
                }`}
              onClick={() => {
                setNegotiableDropdownOpen(
                  !negotiableDropdownOpen
                );

                setCategoryDropdownOpen(false);
                setTrafficDropdownOpen(false);
                setAudienceDropdownOpen(false);
                setPricingDropdownOpen(false);
                setPlacementDropdownOpen(false);
              }}
            >
              <span>
                {filters.negotiable === true
                  ? "Yes"
                  : filters.negotiable === false
                    ? "No"
                    : "Select option"}
              </span>

              <span
                className={`ad-dropdown-arrow ${negotiableDropdownOpen
                  ? "rotate"
                  : ""
                  }`}
              >
                ▼
              </span>
            </div>

            {negotiableDropdownOpen && (
              <div className="ad-custom-dropdown-menu">
                {[
                  {
                    value: true,
                    label: "Yes",
                  },
                  {
                    value: false,
                    label: "No",
                  },
                ].map((item) => (
                  <div
                    key={String(item.value)}
                    className={`ad-custom-dropdown-item ${filters.negotiable ===
                      item.value
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        negotiable:
                          item.value,
                      }));

                      setNegotiableDropdownOpen(
                        false
                      );
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="ad-filter-actions">
          <button className="ad-apply-btn" onClick={handleApplyFilters}>
            Apply Filters
          </button>
          <button className="ad-reset-btn" onClick={handleReset}>
            <ReloadOutlined style={{ marginRight: 6 }} />
            Reset All
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ad-page">
      <div className="ad-container">

        {/* Hero */}
        <header className="ad-hero">
          <div className="ad-hero-content">
            <span className="ad-hero-tag">Advertising Marketplace</span>
            <h1 className="ad-hero-title">Advertising Spaces</h1>
            <p className="ad-hero-desc">
              Discover premium billboard and ad placements across cities
            </p>
          </div>
          <div className="ad-hero-decoration" aria-hidden="true">
            <div className="ad-hero-circle ad-hero-circle--lg" />
            <div className="ad-hero-circle ad-hero-circle--sm" />
          </div>
        </header>

        {/* Search & Filter Bar */}
        <section className="ad-bar">
          <div className="ad-bar-top">
            <div className="ad-bar-count">
              <strong>{pagination.total.toLocaleString()}</strong> ads available
            </div>
            {activeFiltersCount > 0 && (
              <button className="ad-clear-all" onClick={handleReset}>
                Clear all ({activeFiltersCount})
              </button>
            )}
          </div>

          <div className="ad-bar-controls">
            <div className="ad-search-field">
              <SearchOutlined className="ad-search-ico" />

              {/*
               * UPGRADED AutoComplete
               *
               * - onChange  → handleSearchChange (marks location invalid, fetches suggestions)
               * - onSelect  → handleSearchSelect (marks location valid, NO auto-fetch)
               * - value     → searchInputValue (controlled by hook)
               * - inner input onKeyDown → only fires handleSearchBarApply when valid
               */}
              <div
                className="ad-search-custom"
                ref={filterDropdownRef}
              >
                <input
                  type="text"
                  className="ad-search-input"
                  placeholder="Search by location, ad type, traffic, audience, or category..."
                  maxLength={200}
                  value={searchInputValue}
                  onFocus={handleSearchFocus}
                  onChange={(e) => {
                    const value = e.target.value;

                    handleSearchChange(value);

                    setFilters((prev) => ({
                      ...prev,
                      search: value.slice(0, 200),
                      locality: "",
                      latitude: null,
                      longitude: null,
                      category: "",
                      minPrice: "",
                      maxPrice: "",
                      trafficLevel: "",
                      audienceType: [],
                      pricingUnit: "",
                      placementPosition: "",
                      vehicleType: "",
                      negotiable: null,
                    }));

                    clearAllFieldErrors();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchBarApply();
                    }
                  }}
                />

                {searchSuggestions.length > 0 && (
                  <div className="ad-search-dropdown-custom">
                    {searchSuggestions.map((item, index) => (
                      <div
                        key={index}
                        className="ad-search-dropdown-item"
                        onClick={() => {
                          handleSearchSelect(item.value);
                          setSearchSuggestions([]);
                        }}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/*
             * UPGRADED Search button
             *
             * - disabled when no valid dropdown location has been selected
             * - onClick only calls handleSearchBarApply (which also guards validity)
             */}
            <button
              className="ad-search-btn"
              onClick={handleSearchBarApply}
            >
              Search
            </button>

            <button className="ad-filter-btn" onClick={() => setFilterOpen(true)}>
              <FilterOutlined />
              <span className="ad-filter-btn-label">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="ad-filter-badge">{activeFiltersCount}</span>
              )}
            </button>

            <Modal
              title="Filter Ads"
              open={filterOpen}
              onCancel={() => {
                setFilterOpen(false);
                clearAllFieldErrors();
              }}
              footer={null}
              centered
              width={600}
              className="ad-filter-modal"
              mask={{
                closable: true,
              }}
              modalRender={(modal) => (
                <div ref={filterModalBodyRef}>
                  {modal}
                </div>
              )}
            >
              {filterDrawer}
            </Modal>
          </div>
        </section>

        {/* Grid / States */}
        {loading ? (
          <div className="ad-state-wrap">
            <Spin size="large" />
            <p className="ad-state-msg">Loading ads…</p>
          </div>
        ) : ads.length === 0 ? (
          <div className="ad-state-wrap">
            <Empty description={<span className="ad-state-msg">No ads found</span>} />
          </div>
        ) : (
          <>
            <Row gutter={[20, 20]} className="ad-grid">
              {ads.map((ad) => (
                <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={6} key={ad._id}>
                  <article className="ad-card">
                    <div className="ad-card-thumb">
                      <img
                        alt={ad.title}
                        src={
                          ad.thumbnail ||
                          "https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800&q=80"
                        }
                        className="ad-card-img"
                        loading="lazy"
                      />
                      <div className="ad-card-overlay" />
                      <div className="ad-card-tags">
                        <span className="ad-tag ad-tag--listing">Ad Space</span>
                        {ad.category && (
                          <span className="ad-tag ad-tag--type">
                            {ad.category.replace("_", " ")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ad-card-body">
                      <div className="ad-card-location">
                        <EnvironmentOutlined className="ad-loc-ico" />
                        <span className="ad-loc-text">
                          {[ad.location?.locality, ad.location?.city, ad.location?.state]
                            .filter(Boolean)
                            .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
                            .join(", ")}
                        </span>
                      </div>

                      <h3 className="ad-card-name">{ad.title}</h3>

                      <div className="ad-card-price">
                        <span className="ad-price-num">
                          ₹{Number(ad.pricing?.price || 0).toLocaleString("en-IN")}
                        </span>
                        <span className="ad-price-unit">
                          /{ad.pricing?.unit?.replace("per_", "") || "day"}
                        </span>
                      </div>

                      <div className="ad-card-footer">
                        <Link to={`/ads/${ad.slug}`} className="ad-card-cta">
                          View details →
                        </Link>
                      </div>
                    </div>
                  </article>
                </Col>
              ))}
            </Row>

            <div className="ad-pagination">
              <Pagination
                current={pagination.current}
                pageSize={15}
                total={pagination.total}
                onChange={handlePageChange}
                responsive
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Adss;